import { useQuery } from "@tanstack/react-query";

import { API_BASE } from "@/services/api/client";
import { cn } from "@/utils/cn";

async function probeHealth(): Promise<boolean> {
  const ctrl = new AbortController();
  const tm = window.setTimeout(() => ctrl.abort(), 4_000);
  try {
    const resp = await fetch(`${API_BASE}/health`, { signal: ctrl.signal });
    return resp.ok;
  } catch {
    return false;
  } finally {
    window.clearTimeout(tm);
  }
}

interface Props {
  className?: string;
}

export function StatusPill({ className }: Props) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["api-health"],
    queryFn: probeHealth,
    refetchInterval: 30_000,
    staleTime: 25_000,
  });

  let dot = "bg-warmgray-300";
  let label = "Checking…";
  let pulse = false;

  if (isLoading) {
    dot = "bg-oxblood-300";
    label = "Checking…";
    pulse = true;
  } else if (isError || data === false) {
    dot = "bg-oxblood-500";
    label = "API offline";
  } else if (data === true) {
    dot = "bg-verdigris";
    label = "API online";
  }

  return (
    <a
      href="https://fly.io/apps/eryndal-graph-rag/monitoring"
      rel="noreferrer noopener"
      target="_blank"
      className={cn(
        "inline-flex items-center gap-2 text-xs text-warmgray-500 hover:text-oxblood-500",
        className,
      )}
    >
      <span
        className={cn(
          "inline-block h-2 w-2 rounded-full",
          dot,
          pulse && "animate-ery-shimmer",
        )}
      />
      <span>{label}</span>
    </a>
  );
}
